import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';

export const meta = {
  id: 'ticket_trend',
  category: 'tickets',
  titleKey: 'dashboard.widgets.ticketTrend',
  descriptionKey: 'dashboard.widgets.ticketTrendDesc',
  defaultSize: 'xl',
  icon: TrendingUp,
};

export default function TicketTrendWidget({ ctx }) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const isMobile = useIsMobile();
  const [trendDays, setTrendDays] = useState(30);

  const { data: extendedTrend } = useQuery({
    queryKey: ['dashboard-ticket-trend', trendDays],
    queryFn: () => api.get('/dashboard/ticket-trend', { params: { days: trendDays } }).then(r => r.data),
    enabled: isAdmin && trendDays !== 30,
    refetchInterval: 60000,
  });

  const activeTrendRows = (isAdmin && trendDays !== 30 && extendedTrend?.trend) ? extendedTrend.trend : (ctx?.ticketTrend || []);
  const trendBucket = (isAdmin && trendDays !== 30 && extendedTrend?.bucket) ? extendedTrend.bucket : 'day';
  const trendData = (activeTrendRows || []).map(d => ({
    date: format(new Date(d.day), 'dd/MM'),
    count: parseInt(d.tickets_created, 10) || 0,
    resolved: parseInt(d.tickets_resolved, 10) || 0,
  }));

  if (trendData.length === 0) return null;

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title">{t('dashboard.ticketTrend')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', marginRight: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--c-text-muted)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--c-brand)' }} />
            {t('dashboard.ticketsCreated')}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--c-text-muted)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--c-resolved)' }} />
            {t('dashboard.ticketsResolvedSeries')}
          </span>
        </div>
        {isAdmin ? (
          <select
            value={trendDays}
            onChange={(e) => setTrendDays(parseInt(e.target.value, 10))}
            style={{ fontSize: 11, padding: '2px 6px', background: 'var(--c-surface)', color: 'var(--c-text-muted)', border: '1px solid var(--c-border)', borderRadius: 6 }}
          >
            <option value={30}>{t('dashboard.trendDays30')}</option>
            <option value={60}>{t('dashboard.trendDays60')}</option>
            <option value={90}>{t('dashboard.trendDays90')}</option>
            <option value={180}>{t('dashboard.trendDays180')}</option>
            <option value={365}>{t('dashboard.trendDays365')}</option>
          </select>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{t('dashboard.last30Days')}</span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 150 : 210}>
        <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--c-brand)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--c-brand)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="trendGradientResolved" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--c-resolved)" stopOpacity={0.22} />
              <stop offset="95%" stopColor="var(--c-resolved)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" tick={{ fill: 'var(--c-text-muted)', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fill: 'var(--c-text-muted)', fontSize: 10 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, fontSize: 12 }}
            formatter={(value, name) => [value, name === 'resolved' ? t('dashboard.ticketsResolvedSeries') : t('dashboard.ticketsCreated')]}
          />
          <Area type="monotone" dataKey="count" stroke="var(--c-brand)" strokeWidth={2} fill="url(#trendGradient)" />
          <Area type="monotone" dataKey="resolved" stroke="var(--c-resolved)" strokeWidth={2} fill="url(#trendGradientResolved)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
